import { Amplify } from 'aws-amplify';
import { isWeb } from './util/platform';

import '@aws-amplify/react-native';          // RN polyfills
import 'react-native-url-polyfill/auto';     // URL/URLSearchParams for Hermes

// ── 0) single-run guard (avoids Fast Refresh double config)
if (!globalThis.__AMP_CFG__) {

  const restFromExports = (awsconfig.aws_cloud_logic_custom ?? []).reduce((acc, e) => {
    acc[e.name] = { endpoint: e.endpoint, region: e.region };
    return acc;
  }, {});

  Amplify.configure({
    ...awsconfig,
    API: {
      ...(awsconfig).API,      // keep anything that may already exist
      REST: {
        ...((awsconfig)?.API?.REST ?? {}),
        ...restFromExports,
      },
    },
    // If you’re on Next.js (SSR), keep this:
    ssr: true,
  });

  // ── dev-only probe: show what Amplify sees *now*
  if (__DEV__) {
     
    console.log('[BOOT] exports.aws_cloud_logic_custom =', awsconfig.aws_cloud_logic_custom);
     
    console.log('[BOOT] runtime API.REST =', Object.keys(Amplify.getConfig()?.API?.REST ?? {}));
  }
  globalThis.__AMP_CFG__ = true;
}

// Silence stray console.log noise in production web builds, where the browser console is
// user-visible. Done once at startup rather than during a component render, and never in
// __DEV__ so web debugging still works. util/logger already self-disables outside __DEV__.
if (!__DEV__ && isWeb()) {
  console.log = function () {};
}


// now import other libs / splash / fonts, etc.
import React from 'react';
import * as SplashScreen from 'expo-splash-screen';
import * as Font from 'expo-font';
import { RootSiblingParent } from 'react-native-root-siblings';
import { ActionSheetProvider } from '@expo/react-native-action-sheet';
import { StatusBar, View, Keyboard } from 'react-native'
import { SafeAreaProvider } from 'react-native-safe-area-context';
import Constants, { ExecutionEnvironment } from 'expo-constants';

import awsconfig from './src/aws-exports';
import 'setimmediate';
import ErrorBoundary from './components/ErrorBoundary';

// ⬇️ import the rest *after* config is set
const Navigator = require('./routes/RootStack').default;
const { initI18n } = require('./i18n');

SplashScreen.preventAutoHideAsync();

export default class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      fontsLoaded: false,
    };
  }

  async loadAssetsAsync() {
    // Resolve the language alongside the fonts, behind the same splash gate, so
    // the first paint is already translated — no flash of English. Runs in
    // parallel because neither depends on the other.
    //
    // Failing to pick a language must never strand the user on the splash
    // screen, so this settles to English rather than rejecting.
    const i18nReady = initI18n().catch((error) => {
      console.warn('i18n init failed; continuing in English:', error?.message || error);
    });

    await Font.loadAsync({
      'Poppins-Medium': require('./assets/fonts/Poppins-Medium.ttf'),
      'Poppins-Bold': require('./assets/fonts/Poppins-Bold.ttf'),
      'Poppins-Thin': require('./assets/fonts/Poppins-Thin.ttf'),
      'Poppins-SemiBold': require('./assets/fonts/Poppins-SemiBold.ttf'),
      // Uncomment if you need other poppins fonts! Should work automatically :)
      'Poppins-Black': require('./assets/fonts/Poppins-Black.ttf'),
      'Poppins-ExtraBold': require('./assets/fonts/Poppins-ExtraBold.ttf'),
      'Poppins-ExtraLight': require('./assets/fonts/Poppins-ExtraLight.ttf'),
      'Poppins-Light': require('./assets/fonts/Poppins-Light.ttf'),
      'Poppins-Regular': require('./assets/fonts/Poppins-Regular.ttf'),
    });
    await i18nReady;
    await SplashScreen.hideAsync();
    this.setState({ fontsLoaded: true });
  }

  componentDidMount() {
    this.loadAssetsAsync();
    this.initClarity();
  }

  initClarity() {
    if (globalThis.__CLARITY_INIT__) return;
    globalThis.__CLARITY_INIT__ = true;

    // Separate Clarity projects keep web (DOM replay/heatmaps) and native
    // (view-hierarchy replay) data clean — metrics don't compare across them.
    const CLARITY_ID_WEB = 'xq1obinxfn'; // "Moneta Web" project
    const CLARITY_ID_MOBILE = 'xq190r1j0m'; // "Moneta Mobile" project

    if (isWeb()) {
      // Web uses Clarity's standard JS tag (the RN SDK is native-only).
      // Injected at runtime so it survives every `expo export:web` rebuild.
      (function (c, l, a, r, i, t, y) {
        c[a] =
          c[a] ||
          function () {
            (c[a].q = c[a].q || []).push(arguments);
          };
        t = l.createElement(r);
        t.async = 1;
        t.src = 'https://www.clarity.ms/tag/' + i;
        y = l.getElementsByTagName(r)[0];
        y.parentNode.insertBefore(t, y);
      })(window, document, 'clarity', 'script', CLARITY_ID_WEB);
      return;
    }

    // Clarity's native SDK isn't bundled in Expo Go (storeClient), so calling it
    // there crashes the app. Skip in Expo Go — a dev/production build includes it.
    if (Constants.executionEnvironment === ExecutionEnvironment.StoreClient) {
      console.log('Skipping Clarity init: native module unavailable in Expo Go');
      return;
    }

    // Belt-and-suspenders: if the native module is missing for any other reason,
    // swallow the error rather than taking down the whole app.
    try {
      const Clarity = require('@microsoft/react-native-clarity');
      Clarity.initialize(CLARITY_ID_MOBILE, {
        logLevel: __DEV__ ? Clarity.LogLevel.Verbose : Clarity.LogLevel.None,
      });
    } catch (err) {
      console.warn('Clarity init failed; continuing without it:', err?.message || err);
    }
  }

  handleUnhandledTouches() {
    Keyboard.dismiss();
    return false;
  }

  render() {
    if (this.state.fontsLoaded) {
      return (
        <ErrorBoundary>
          <SafeAreaProvider>
            <RootSiblingParent>
              <ActionSheetProvider>
                <View style={{ flex: 1 }}>
                  <StatusBar backgroundColor={'#000'} barStyle="light-content" />
                  <Navigator />
                </View>
              </ActionSheetProvider>
            </RootSiblingParent>
          </SafeAreaProvider>
        </ErrorBoundary>
      );
    }
  }
}
